import { OKNGainResult, OKNRawSample, OKNVector3 } from "types/testTypes";

const RAD_TO_DEG = 180 / Math.PI;
const REST_PHASE = "rest";

export function oknHorizontalAngleDeg(vector?: OKNVector3 | null): number | null {
  if (!vector || !Number.isFinite(vector.x) || !Number.isFinite(vector.z) || vector.z === 0) {
    return null;
  }
  return Math.atan2(vector.x, vector.z) * RAD_TO_DEG;
}

export function oknVerticalAngleDeg(vector?: OKNVector3 | null): number | null {
  if (!vector || !Number.isFinite(vector.y) || !Number.isFinite(vector.z) || vector.z === 0) {
    return null;
  }
  return Math.atan2(vector.y, vector.z) * RAD_TO_DEG;
}

export function isHorizontalAxis(axis?: string | null): boolean {
  return (axis ?? "").toLowerCase().startsWith("h");
}

export function oknDirectionLabelKey(direction?: string | null): string {
  switch ((direction ?? "").toLowerCase()) {
    case "lefttoright":
      return "okn.directionRight";
    case "righttoleft":
      return "okn.directionLeft";
    case "bottomtotop":
      return "okn.directionUp";
    case "toptobottom":
      return "okn.directionDown";
    default:
      return "okn.directionUnknown";
  }
}

export interface OKNTracePoint {
  t: number;
  left: number | null;
  right: number | null;
}

export interface OKNSegmentTrace {
  segmentIndex: number;
  axis: string;
  direction: string;
  speedDegPerSecond: number;
  horizontal: boolean;
  points: OKNTracePoint[];
}

export function buildOKNSegmentTraces(rawSamples?: OKNRawSample[]): OKNSegmentTrace[] {
  if (!rawSamples?.length) return [];

  const bySegment = new Map<number, OKNRawSample[]>();
  for (const sample of rawSamples) {
    if ((sample.phase ?? "").toLowerCase() === REST_PHASE) continue;
    if (!(sample.speedDegPerSecond > 0)) continue;
    const list = bySegment.get(sample.segmentIndex);
    if (list) list.push(sample);
    else bySegment.set(sample.segmentIndex, [sample]);
  }

  const traces: OKNSegmentTrace[] = [];
  for (const [segmentIndex, samples] of bySegment) {
    const first = samples[0];
    const horizontal = isHorizontalAxis(first.axis);
    const angle = horizontal ? oknHorizontalAngleDeg : oknVerticalAngleDeg;

    const points: OKNTracePoint[] = samples.map((s) => ({
      t: Number(s.elapsedSegmentTime?.toFixed?.(3) ?? s.elapsedSegmentTime),
      left: s.isEyeTrackingValid ? round2(angle(s.leftGazeVector)) : null,
      right: s.isEyeTrackingValid ? round2(angle(s.rightGazeVector)) : null,
    }));

    traces.push({
      segmentIndex,
      axis: first.axis,
      direction: first.direction,
      speedDegPerSecond: first.speedDegPerSecond,
      horizontal,
      points,
    });
  }

  traces.sort((a, b) => a.segmentIndex - b.segmentIndex);
  return traces;
}

export interface OKNGainSeriesPoint {
  t: number;
  left: number | null;
  right: number | null;
}

export interface OKNGainSegmentSeries {
  segmentIndex: number;
  axis: string;
  direction: string;
  speedDps: number;
  points: OKNGainSeriesPoint[];
}

export interface OKNGainSummaryRow {
  segmentIndex: number;
  axis: string;
  direction: string;
  speedDps: number;
  leftMean: number | null;
  leftStd: number | null;
  rightMean: number | null;
  rightStd: number | null;
}

interface OKNSeriesEntryLike {
  eye: string;
  axis: string;
  direction: string;
  speedDps: number;
  segmentIndex: number;
  perSlowPhase: { tMid: number; tStart: number; gain: number; accepted: boolean }[];
  summary: { gainMean: number | null; gainStd: number | null };
}

function groupSeriesBySegment(
  oknGain: OKNGainResult,
): Map<number, { left?: OKNSeriesEntryLike; right?: OKNSeriesEntryLike }> {
  const bySegment = new Map<number, { left?: OKNSeriesEntryLike; right?: OKNSeriesEntryLike }>();
  for (const entry of oknGain.series) {
    const seg = bySegment.get(entry.segmentIndex) ?? {};
    if (entry.eye === "left") seg.left = entry;
    else if (entry.eye === "right") seg.right = entry;
    bySegment.set(entry.segmentIndex, seg);
  }
  return bySegment;
}

export function buildOKNGainSeries(
  oknGain: OKNGainResult | null | undefined,
  rawSamples?: OKNRawSample[],
): OKNGainSegmentSeries[] {
  if (!oknGain || !oknGain.series?.length) return [];

  const bySegment = groupSeriesBySegment(oknGain);
  const result: OKNGainSegmentSeries[] = [];

  for (const [segmentIndex, { left, right }] of bySegment) {
    let segmentStart: number | null = null;
    if (rawSamples?.length) {
      for (const sample of rawSamples) {
        if (sample.segmentIndex !== segmentIndex) continue;
        if (String(sample.phase).toLowerCase() === REST_PHASE) continue;
        if (segmentStart == null || sample.timestamp < segmentStart) {
          segmentStart = sample.timestamp;
        }
      }
    }
    if (segmentStart == null) {
      for (const entry of [left, right]) {
        if (!entry) continue;
        for (const sp of entry.perSlowPhase) {
          if (!sp.accepted) continue;
          if (segmentStart == null || sp.tStart < segmentStart) {
            segmentStart = sp.tStart;
          }
        }
      }
    }
    if (segmentStart == null) segmentStart = 0;

    const points: OKNGainSeriesPoint[] = [];
    if (left) {
      for (const sp of left.perSlowPhase) {
        if (!sp.accepted) continue;
        points.push({ t: round2Num(sp.tMid - segmentStart), left: round2(sp.gain), right: null });
      }
    }
    if (right) {
      for (const sp of right.perSlowPhase) {
        if (!sp.accepted) continue;
        points.push({ t: round2Num(sp.tMid - segmentStart), left: null, right: round2(sp.gain) });
      }
    }

    if (!points.length) continue;

    points.sort((a, b) => a.t - b.t);

    const ref = left ?? right!;
    result.push({
      segmentIndex,
      axis: ref.axis,
      direction: ref.direction,
      speedDps: ref.speedDps,
      points,
    });
  }

  result.sort((a, b) => a.segmentIndex - b.segmentIndex);
  return result;
}

export function buildOKNGainSummaryRows(
  oknGain: OKNGainResult | null | undefined,
): OKNGainSummaryRow[] {
  if (!oknGain || !oknGain.series?.length) return [];

  const bySegment = groupSeriesBySegment(oknGain);
  const rows: OKNGainSummaryRow[] = [];

  for (const [segmentIndex, { left, right }] of bySegment) {
    const ref = left ?? right!;
    rows.push({
      segmentIndex,
      axis: ref.axis,
      direction: ref.direction,
      speedDps: ref.speedDps,
      leftMean: left ? left.summary.gainMean : null,
      leftStd: left ? left.summary.gainStd : null,
      rightMean: right ? right.summary.gainMean : null,
      rightStd: right ? right.summary.gainStd : null,
    });
  }

  rows.sort((a, b) => a.segmentIndex - b.segmentIndex);
  return rows;
}

function round2(value: number | null): number | null {
  return value == null ? null : Number(value.toFixed(2));
}

function round2Num(value: number): number {
  return Number(value.toFixed(2));
}
